import type { CellEditRequestEvent, GridApi, ICellRendererParams } from "ag-grid-community";
import { BracesIcon, PencilIcon, PlusIcon, Table2Icon, VariableIcon } from "lucide-react";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { v4 as uuidv4 } from "uuid";

import {
  EVALUATE_GRID_DISPLAY,
  EVALUATE_GRID_EDITING,
  EVALUATE_GRID_OPTIONS,
  EVALUATE_GRID_ROW_NUMBERS,
  EVALUATE_GRID_SELECTION,
  type EvaluateAdHocRow,
  executionMetadataChildHeader,
  executionMetadataValueFormatter,
  getEvaluateExecutionMetric,
  getMappedInputVariableValue,
  getPromptCellPayload,
  getSourceSheetCellValue,
  isEvaluateInputColumn,
  isExecutionMetadataChildColumn,
  isExecutionMetadataMetricKey,
  PROMPT_TEMPLATE_COLUMN_TYPE,
  toCellDisplayText,
} from "./evaluate-drawer-utils";
import useAddEvaluateInputSets from "./useAddEvaluateInputSets";
import { useEvaluateScope } from "./useEvaluateScope";

import { Sheet } from "@/components/Sheet";
import type { SheetColumn } from "@/components/Sheet";
import { formatVariableName } from "@/components/PowerBlock/Block/InputVariablesField/types";
import type { VariableSet } from "@/components/Playground/types";
import { useInputVariables } from "@/components/Playground/hooks/useInputVariables";
import { getVariableValueIsFilled } from "@/components/Playground/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PlaygroundEvaluateRunDetail,
  PlaygroundEvaluateSheetReference,
  useUpdateEvaluateSheetReference,
  type PlaygroundEvaluateColumn,
} from "@/queries/playground/evaluate";
import {
  CellContentDialog,
  useCellContentDialog,
} from "@/routes/smart-table/components/SmartTablePage/Sheet/CellRenderer/CellContentDialog";
import { createReadOnlyPreviewColumnDefs } from "@/routes/smart-table/components/SmartTablePage/RightPanel/VersionPreviewSidebarContent/previewColumnDefs";
import { assignCompositionSourceFields } from "@/routes/smart-table/utils/compositionColumns";
import { resolveCellDisplayState } from "@/routes/smart-table/utils/cellDisplayState";
import {
  getSmartTableGroupedHeaderGridOptions,
  groupConsecutiveInputColumns,
  hasGroupedColumns,
  isSmartTableColumnGroupDef,
  type SmartTableColumnDef,
} from "@/routes/smart-table/utils/smartTableColumnGroups";
import { mapMetricGroupColumnDefs } from "@/routes/smart-table/utils/smartTableGroupedColumns";
import {
  CellStatus,
  ColumnType,
  type GridRow,
  type SmartColumn,
} from "@/queries/smart-tables/types";
import { cn } from "@/lib/utils";

interface EvaluateAdHocSheetProps {
  displayedRun:
    | (Pick<
        PlaygroundEvaluateRunDetail,
        "status" | "input_variable_mappings_snapshot" | "template_override_snapshot"
      > & { rows?: PlaygroundEvaluateRunDetail["rows"] })
    | null
    | undefined;
  isRunDetailLoading: boolean;
  reference: PlaygroundEvaluateSheetReference;
  variableSets: VariableSet[];
  sourceColumns: SmartColumn[];
  sourceRows: GridRow[];
  sourceRowCount: number;
  isSourceLoading: boolean;
  isPromptOutputStale: boolean;
  canEdit: boolean;
  gridApiRef: MutableRefObject<GridApi | null>;
  onConfigureInputSet: (setId?: string) => void;
}

interface InputOverrideEmptyCellProps extends ICellRendererParams<EvaluateAdHocRow> {
  sourceColumn: PlaygroundEvaluateColumn;
  reference: PlaygroundEvaluateSheetReference;
  variableSets: VariableSet[];
  canEdit: boolean;
}

interface InputValueCellProps extends ICellRendererParams<EvaluateAdHocRow> {
  columnId: string;
  variableName: string;
  canEdit: boolean;
  onClearOverride: (setId: string, variableName: string) => void;
}

const InputValueCell = ({
  value,
  data,
  columnId,
  variableName,
  canEdit,
  onClearOverride,
  api,
  node,
  column,
}: InputValueCellProps) => {
  const usesSourceValue = Boolean(data?.[`_source_${columnId}`]);
  const isMissingValue = Boolean(data?.[`_missing_${columnId}`]);
  const hasSheetDefault = Boolean(data?.[`_has_source_${columnId}`]);
  const inputSetId = data?._variableSetId;
  const displayValue = toCellDisplayText(value);
  const columnLabel = column?.getColDef().headerName || columnId;
  const startEditing = () => {
    if (!canEdit || node.rowIndex === null) {
      return;
    }
    api.startEditingCell({ rowIndex: node.rowIndex, colKey: columnId });
  };

  let provenance: ReactNode = (
    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-cyan-700">
      <PencilIcon className="h-3 w-3" aria-hidden="true" />
      Override
    </span>
  );
  if (canEdit && hasSheetDefault && inputSetId) {
    provenance = (
      <button
        type="button"
        className="group/override grid min-w-[76px] shrink-0 rounded bg-cyan-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-cyan-700 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300"
        aria-label={`Override for ${columnLabel}. Use the sheet default instead.`}
        title="Click to use the sheet default."
        onMouseDown={event => event.stopPropagation()}
        onClick={event => {
          event.stopPropagation();
          onClearOverride(inputSetId, variableName);
        }}
      >
        <span
          aria-hidden="true"
          className="col-start-1 row-start-1 inline-flex items-center justify-center gap-1 transition-opacity group-hover/override:opacity-0 group-focus-visible/override:opacity-0"
        >
          <PencilIcon className="h-3 w-3" />
          Override
        </span>
        <span
          aria-hidden="true"
          className="col-start-1 row-start-1 inline-flex items-center justify-center gap-1 opacity-0 transition-opacity group-hover/override:opacity-100 group-focus-visible/override:opacity-100"
        >
          <Table2Icon className="h-3 w-3" />
          Use default
        </span>
      </button>
    );
  }
  if (isMissingValue && canEdit) {
    provenance = (
      <button
        type="button"
        className="inline-flex shrink-0 cursor-text items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-600 transition-colors hover:bg-cyan-50 hover:text-cyan-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300"
        aria-label={`Add a value for ${columnLabel}`}
        title="Add a value to this input set"
        onMouseDown={event => event.stopPropagation()}
        onClick={event => {
          event.stopPropagation();
          startEditing();
        }}
      >
        <PencilIcon className="h-3 w-3" />
        Add Value
      </button>
    );
  } else if (isMissingValue) {
    provenance = null;
  } else if (usesSourceValue && canEdit) {
    provenance = (
      <button
        type="button"
        className="group/default grid min-w-[68px] shrink-0 cursor-text rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-500 transition-colors hover:bg-cyan-50 hover:text-cyan-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-cyan-300"
        aria-label={`Using the sheet default for ${columnLabel}. Add an override.`}
        title="Using the sheet default. Click to add an override."
        onMouseDown={event => event.stopPropagation()}
        onClick={event => {
          event.stopPropagation();
          startEditing();
        }}
      >
        <span
          aria-hidden="true"
          className="col-start-1 row-start-1 inline-flex items-center justify-center gap-1 transition-opacity group-hover/default:opacity-0 group-focus-visible/default:opacity-0"
        >
          <Table2Icon className="h-3 w-3" />
          Default
        </span>
        <span
          aria-hidden="true"
          className="col-start-1 row-start-1 inline-flex items-center justify-center gap-1 opacity-0 transition-opacity group-hover/default:opacity-100 group-focus-visible/default:opacity-100"
        >
          <PencilIcon className="h-3 w-3" />
          Override
        </span>
      </button>
    );
  } else if (usesSourceValue) {
    provenance = (
      <span className="inline-flex shrink-0 items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-500">
        <Table2Icon className="h-3 w-3" aria-hidden="true" />
        Default
      </span>
    );
  }

  return (
    <div className="flex h-full min-w-0 flex-1 items-center justify-between gap-2">
      <span
        className={cn(
          "min-w-0 truncate",
          usesSourceValue && !isMissingValue && "italic text-gray-400",
          isMissingValue && "text-gray-400",
        )}
      >
        {isMissingValue ? "—" : displayValue || "Empty"}
      </span>
      {provenance}
    </div>
  );
};

const InputSetHeader = ({ displayName }: { displayName?: string }) => (
  <div className="flex h-full w-full min-w-0 items-center gap-1.5">
    <VariableIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/90" aria-hidden="true" />
    <span className="truncate text-sm font-medium">{displayName ?? "Input set"}</span>
  </div>
);

interface InputSetCellProps extends ICellRendererParams<EvaluateAdHocRow> {
  canEdit: boolean;
  onConfigureInputSet: (setId?: string) => void;
}

const InputSetCell = ({ data, canEdit, onConfigureInputSet }: InputSetCellProps) => {
  if (!data?._variableSetId) {
    return <span className="text-xs text-gray-400">Sheet preview</span>;
  }

  return (
    <button
      type="button"
      className="flex h-full w-full min-w-0 items-center gap-2 text-left text-xs font-medium text-gray-600 transition-colors hover:text-gray-900 focus:outline-none disabled:cursor-default disabled:hover:text-gray-600"
      disabled={!canEdit}
      onClick={() => onConfigureInputSet(data._variableSetId)}
      title="Edit this input set"
    >
      <VariableIcon className="h-3.5 w-3.5 shrink-0 text-cyan-600" />
      <span className="truncate">{data._variableSetName || `Set ${data._rowIndex + 1}`}</span>
    </button>
  );
};

const toSuggestedVariableName = (columnTitle: string) =>
  columnTitle
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "input";

const InputOverrideEmptyCell = ({
  sourceColumn,
  reference,
  variableSets,
  canEdit,
  value,
}: InputOverrideEmptyCellProps) => {
  const { playground, queryScope } = useEvaluateScope();
  const { ensureVariable } = useInputVariables();
  const updateReference = useUpdateEvaluateSheetReference(queryScope);
  const [open, setOpen] = useState(false);
  const [isCreatingVariable, setIsCreatingVariable] = useState(false);
  const [newVariableName, setNewVariableName] = useState(() =>
    toSuggestedVariableName(sourceColumn.title),
  );
  const variables = Array.from(
    new Set(
      variableSets.flatMap(variableSet =>
        variableSet.variables.map(variable => variable.key).filter(Boolean),
      ),
    ),
  );
  const mappableColumnIds = new Set(
    reference.columns.filter(isEvaluateInputColumn).map(candidate => candidate.id),
  );
  const currentMappings = Object.fromEntries(
    Object.entries(reference.input_variable_mappings).flatMap(([variable, mapping]) =>
      mappableColumnIds.has(mapping.column_id) ? [[variable, mapping.column_id]] : [],
    ),
  );
  const mappedColumnTitleByVariable = new Map(
    Object.entries(currentMappings).map(([variable, columnId]) => [
      variable,
      reference.columns.find(candidate => candidate.id === columnId)?.title,
    ]),
  );

  const assignVariable = (variable: string, onSuccess?: () => void) => {
    updateReference.mutate(
      {
        referenceId: reference.id,
        body: {
          input_variable_mappings: {
            ...Object.fromEntries(
              Object.entries(currentMappings).map(([key, columnId]) => [
                key,
                { column_id: columnId },
              ]),
            ),
            [variable]: { column_id: sourceColumn.id },
          },
        },
      },
      {
        onSuccess: () => {
          onSuccess?.();
          setOpen(false);
        },
      },
    );
  };
  const normalizedVariableName = newVariableName.trim();
  const variableAlreadyExists = variables.includes(normalizedVariableName);
  const showCreateVariable = variables.length === 0 || isCreatingVariable;
  const createAndAssignVariable = () => {
    if (!normalizedVariableName || variableAlreadyExists || updateReference.isPending) {
      return;
    }

    assignVariable(normalizedVariableName, () => {
      if (playground.input_variable_sets.length === 0) {
        playground.addEmptyInputVariableSet([normalizedVariableName]);
      } else {
        ensureVariable(normalizedVariableName);
      }
    });
  };

  return (
    <Popover
      open={open}
      onOpenChange={nextOpen => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setIsCreatingVariable(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={!canEdit}
          className="group flex h-full w-full min-w-0 items-center justify-between gap-2 text-left text-xs text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:text-foreground disabled:cursor-default disabled:hover:text-muted-foreground"
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          title={`Connect ${sourceColumn.title} to an input variable. Until then, the sheet value is shown.`}
        >
          <span className="min-w-0 truncate italic text-gray-400">
            {toCellDisplayText(value) || "Empty"}
          </span>
          <span className="inline-flex shrink-0 items-center gap-1 rounded border border-dashed border-gray-300 bg-white px-1.5 py-0.5 text-[10px] font-medium leading-none text-gray-600 transition-colors group-hover:border-cyan-300 group-hover:text-cyan-700">
            <BracesIcon className="h-3 w-3" aria-hidden="true" />
            Connect
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        avoidCollisions
        collisionPadding={16}
        sticky="always"
        className="mx-0 overflow-y-auto overscroll-contain rounded-xl border-gray-200 p-0 shadow-lg"
        style={{
          width: "min(320px, calc(100vw - 24px), var(--radix-popover-content-available-width))",
          maxHeight:
            "min(80dvh, calc(100dvh - 24px), var(--radix-popover-content-available-height))",
        }}
        onOpenAutoFocus={event => {
          if (variables.length > 0) {
            event.preventDefault();
          }
        }}
        onWheel={event => event.stopPropagation()}
      >
        <div className="border-b border-gray-100 px-3 py-2.5">
          <div className="break-words text-sm font-medium text-gray-800">{sourceColumn.title}</div>
          <div className="mt-0.5 break-words text-xs leading-5 text-gray-500">
            Connect this sheet column to an input variable so every input set can override it.
          </div>
        </div>
        <div>
          {variables.length > 0 && (
            <div className="max-h-64 overflow-y-auto overscroll-contain p-1.5">
              {variables.map(variable => {
                const mappedColumnTitle = mappedColumnTitleByVariable.get(variable);
                const overriddenSetCount = variableSets.filter(variableSet =>
                  getVariableValueIsFilled(
                    variableSet.variables.find(candidate => candidate.key === variable)?.value,
                  ),
                ).length;
                return (
                  <button
                    key={variable}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50 disabled:opacity-50"
                    disabled={updateReference.isPending}
                    onClick={() => assignVariable(variable)}
                  >
                    <span className="min-w-0">
                      <span className="block break-all font-mono text-xs font-medium text-gray-700">
                        {formatVariableName(variable, "f-string")}
                      </span>
                      {mappedColumnTitle && (
                        <span className="block break-words text-[11px] text-gray-400">
                          Move from {mappedColumnTitle} to this column
                        </span>
                      )}
                    </span>
                    <span className="max-w-[40%] break-words text-right text-[11px] text-gray-400">
                      {overriddenSetCount}/{variableSets.length} sets
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="border-t border-gray-100 p-2.5">
            {showCreateVariable ? (
              <div className="space-y-2">
                <label
                  htmlFor={`evaluate-variable-${sourceColumn.id}`}
                  className="block text-xs font-medium text-gray-700"
                >
                  New input variable
                </label>
                <Input
                  id={`evaluate-variable-${sourceColumn.id}`}
                  value={newVariableName}
                  onChange={event => setNewVariableName(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      createAndAssignVariable();
                    }
                  }}
                  placeholder="e.g. customer_message"
                  className="h-8 min-w-0 text-xs"
                />
                {variableAlreadyExists && (
                  <p className="break-words text-[11px] leading-4 text-amber-600">
                    This variable already exists. Choose it above or use a different name.
                  </p>
                )}
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {variables.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      onClick={() => setIsCreatingVariable(false)}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={
                      !normalizedVariableName || variableAlreadyExists || updateReference.isPending
                    }
                    onClick={createAndAssignVariable}
                  >
                    Create and map
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-full justify-start text-xs text-gray-600"
                onClick={() => setIsCreatingVariable(true)}
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Create input variable
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const EvaluateAdHocSheet = observer(
  ({
    displayedRun,
    isRunDetailLoading,
    reference,
    variableSets,
    sourceColumns,
    sourceRows,
    sourceRowCount,
    isSourceLoading,
    isPromptOutputStale,
    canEdit,
    gridApiRef,
    onConfigureInputSet,
  }: EvaluateAdHocSheetProps) => {
    const { playground } = useEvaluateScope();
    const [rowsToAddDraft, setRowsToAddDraft] = useState("1");
    const runRows = displayedRun?.rows || [];
    const runRowsByIndex = new Map(runRows.map(row => [row.row_index, row]));
    const rowIndexes = variableSets.length > 0 ? variableSets.map((_, index) => index) : [0];
    const inputMappings = reference.input_variable_mappings;
    const templateOverride =
      displayedRun?.template_override_snapshot ?? reference.template_override;
    const variableByColumnId = useMemo(() => {
      const mappableColumnIds = new Set(
        reference.columns.filter(isEvaluateInputColumn).map(column => column.id),
      );
      return new Map(
        Object.entries(inputMappings).flatMap(([variable, mapping]) =>
          mappableColumnIds.has(mapping.column_id) ? [[mapping.column_id, variable]] : [],
        ),
      );
    }, [inputMappings, reference.columns]);
    const mappedVariableKeys = useMemo(
      () => Array.from(variableByColumnId.values()),
      [variableByColumnId],
    );
    const { addInputSets, isAddingInputSets, maxInputSets, remainingInputSets } =
      useAddEvaluateInputSets({
        mappedVariableKeys,
        reference,
        sourceRowCount,
        variableSetCount: variableSets.length,
      });
    const templateColumnId = templateOverride?.column_id;
    const columns = sourceColumns;
    const getNextDialogRowIndex = useCallback(
      (currentRowIndex: number, delta: number) => {
        const nextIndex = currentRowIndex + delta;
        if (nextIndex >= 0 && nextIndex < rowIndexes.length) {
          return nextIndex;
        }
        return currentRowIndex;
      },
      [rowIndexes.length],
    );
    const dialog = useCellContentDialog({ getNextRowIndex: getNextDialogRowIndex });
    const clearInputOverride = useCallback(
      (setId: string, variableName: string) => {
        const variableSet = playground.input_variable_sets.find(set => set.id === setId);
        const variable = variableSet?.variables.find(candidate => candidate.key === variableName);
        if (!variable) {
          return;
        }
        playground.updateInputVariable(setId)(variable.id, variableName, "");
      },
      [playground],
    );

    useEffect(() => {
      gridApiRef.current?.refreshCells({ force: true });
    }, [gridApiRef, isPromptOutputStale]);

    const sheetColumns = useMemo<SmartTableColumnDef<EvaluateAdHocRow>[]>(() => {
      const baseColumns = createReadOnlyPreviewColumnDefs<EvaluateAdHocRow>(columns, {
        tableId: reference.smart_table_id,
        sheetId: reference.sheet_id,
        openDialog: dialog.open,
        promptTemplateColumnType: PROMPT_TEMPLATE_COLUMN_TYPE,
      }).map(columnDef => {
        if (isSmartTableColumnGroupDef(columnDef)) {
          return columnDef;
        }

        const columnId = columnDef.colId ?? columnDef.field;
        const sourceColumn = columnId ? columns.find(column => column.id === columnId) : undefined;
        if (!sourceColumn || !isEvaluateInputColumn(sourceColumn)) {
          return columnDef;
        }

        const mappedVariableName = variableByColumnId.get(sourceColumn.id);
        if (mappedVariableName) {
          return {
            ...columnDef,
            editableColumn: canEdit,
            cellRenderer: InputValueCell,
            cellRendererParams: {
              columnId: sourceColumn.id,
              variableName: mappedVariableName,
              canEdit,
              onClearOverride: clearInputOverride,
            },
            tooltipValueGetter: (params: { data?: EvaluateAdHocRow }) => {
              if (params.data?.[`_missing_${sourceColumn.id}`]) {
                return "No value is available from this input set or the sheet.";
              }
              if (params.data?.[`_source_${sourceColumn.id}`]) {
                return canEdit
                  ? "Using the sheet default. Click Default to add an override."
                  : "Using the sheet default.";
              }
              return "This input-set override replaces the sheet default for this run.";
            },
          };
        }

        return {
          ...columnDef,
          editableColumn: false,
          cellRenderer: InputOverrideEmptyCell,
          cellRendererParams: {
            sourceColumn,
            reference,
            variableSets,
            canEdit,
          },
        };
      });

      const columnsById = new Map(columns.map(column => [column.id, column]));
      const visibleColumns = mapMetricGroupColumnDefs(
        columns,
        baseColumns,
        (columnDef, parentColumn) => ({
          ...columnDef,
          children: (columnDef.children ?? []).map(child => {
            if (isSmartTableColumnGroupDef(child as SmartTableColumnDef<EvaluateAdHocRow>)) {
              return child;
            }
            const childLeaf = child as SheetColumn<EvaluateAdHocRow>;
            const childColumn = columnsById.get(childLeaf.colId ?? childLeaf.field ?? "");
            if (!childColumn || !isExecutionMetadataChildColumn(childColumn)) {
              return { ...childLeaf, editableColumn: false };
            }

            const metricKey = childColumn.metric_key;
            const headerName = executionMetadataChildHeader(parentColumn, childColumn);
            const rendererParams =
              typeof childLeaf.cellRendererParams === "object" &&
              childLeaf.cellRendererParams !== null
                ? childLeaf.cellRendererParams
                : {};
            const context =
              typeof childLeaf.context === "object" && childLeaf.context !== null
                ? childLeaf.context
                : {};
            return {
              ...childLeaf,
              header: headerName,
              headerName,
              editableColumn: false,
              headerComponentParams: {
                columnType: ColumnType.TEXT,
                config: childColumn.config ?? null,
                metricKey,
                title: headerName,
              },
              columnGroupShow: "open" as const,
              valueFormatter: executionMetadataValueFormatter(metricKey),
              cellRendererParams: {
                ...rendererParams,
                columnType: ColumnType.TEXT,
                parentColumnId: parentColumn.id,
                metricKey,
              },
              context: {
                ...context,
                columnType: ColumnType.TEXT,
                parentColumnId: parentColumn.id,
                metricKey,
              },
            };
          }),
        }),
      );

      const inputSetColumn: SheetColumn<EvaluateAdHocRow> = {
        colId: "__input_set",
        field: "_variableSetName",
        header: "Input set",
        headerName: "Input set",
        headerComponent: InputSetHeader,
        suppressHeaderMenuButton: true,
        pinned: "left",
        lockPinned: true,
        width: 160,
        minWidth: 140,
        maxWidth: 220,
        editableColumn: false,
        sortable: false,
        resizable: true,
        cellRenderer: InputSetCell,
        cellRendererParams: { canEdit, onConfigureInputSet },
      };

      return [
        inputSetColumn,
        ...groupConsecutiveInputColumns<EvaluateAdHocRow>({
          columns: visibleColumns,
          getColumnType: column => {
            const context = column.context as { columnType?: string } | undefined;
            return context?.columnType;
          },
          isInputColumn: column => {
            const columnId = column.colId ?? column.field;
            const sourceColumn = columnId ? columnsById.get(columnId) : undefined;
            return Boolean(sourceColumn && isEvaluateInputColumn(sourceColumn));
          },
        }),
      ];
    }, [
      canEdit,
      columns,
      reference,
      dialog.open,
      variableByColumnId,
      variableSets,
      onConfigureInputSet,
      clearInputOverride,
    ]);

    const gridOptions = useMemo(
      () => ({
        ...EVALUATE_GRID_OPTIONS,
        ...getSmartTableGroupedHeaderGridOptions(hasGroupedColumns(sheetColumns)),
      }),
      [sheetColumns],
    );

    const rows: EvaluateAdHocRow[] = rowIndexes.map(rowIndex => {
      const runRow = runRowsByIndex.get(rowIndex);
      const sourceRow = sourceRows[rowIndex];
      const variableSet = variableSets[rowIndex];
      const {
        status: promptStatus,
        rawValue: promptRawValue,
        displayValue: promptDisplayValue,
        displayState: promptDisplayState,
      } = getPromptCellPayload(runRow, displayedRun?.status, isPromptOutputStale);
      const row: EvaluateAdHocRow = {
        id: variableSet?.id ?? `${reference.id}:source-preview`,
        _rowIndex: rowIndex,
        _variableSetId: variableSet?.id,
        _variableSetName: variableSet?.name || `Set ${rowIndex + 1}`,
        _pendingHydration: isRunDetailLoading || isSourceLoading,
      };

      for (const column of columns) {
        const variableName = variableByColumnId.get(column.id);
        const sourceValue = getSourceSheetCellValue(sourceRow?.cells[column.id]);
        const mappedValue = getMappedInputVariableValue(
          runRow,
          variableName,
          variableSet,
          sourceValue,
        );
        const isTemplateOverrideColumn = templateColumnId === column.id;
        const metricParentColumnId = column.group_parent_column_id;
        const metricKey = isExecutionMetadataMetricKey(column.metric_key)
          ? column.metric_key
          : null;
        const isTemplateMetricColumn =
          metricKey !== null && metricParentColumnId === templateColumnId;
        const isInputColumn = isEvaluateInputColumn(column);

        const configuredVariable = variableSet?.variables.find(
          variable => variable.key === variableName,
        );
        const hasInputSetOverride = Boolean(
          variableName && getVariableValueIsFilled(configuredVariable?.value),
        );
        const usesSourceValue = isInputColumn && Boolean(variableName) && !hasInputSetOverride;
        const isMissingInputValue = usesSourceValue && sourceValue === undefined;
        const inputValue = variableName ? mappedValue : sourceValue;

        let status = CellStatus.COMPLETED;
        let rawValue: unknown = isInputColumn ? { value: inputValue } : null;
        let originalDisplayValue: unknown = isInputColumn ? toCellDisplayText(inputValue) : null;
        let valueToDisplay: unknown = isInputColumn ? (originalDisplayValue ?? "") : "";
        let errorMessage: string | undefined;

        if (isTemplateOverrideColumn) {
          const { valueToDisplay: promptValueToDisplay, errorMessage: promptErrorMessage } =
            promptDisplayState;
          status = promptStatus;
          rawValue = promptRawValue;
          originalDisplayValue = promptDisplayValue;
          valueToDisplay = promptValueToDisplay;
          errorMessage = promptErrorMessage;
        } else if (isTemplateMetricColumn && metricKey) {
          const metricValue = getEvaluateExecutionMetric(runRow, metricKey);
          status = promptStatus;
          rawValue = metricValue === null ? null : { value: metricValue };
          originalDisplayValue = metricValue;
          valueToDisplay = metricValue ?? "";
          ({ errorMessage } = promptDisplayState);
        } else if (!isInputColumn) {
          const { valueToDisplay: computedValueToDisplay, errorMessage: computedErrorMessage } =
            resolveCellDisplayState({
              status,
              display_value: originalDisplayValue,
              value: rawValue,
            });
          valueToDisplay = computedValueToDisplay;
          errorMessage = computedErrorMessage;
        }

        row[column.id] = assignCompositionSourceFields(row, column, valueToDisplay, {
          errorMessage,
          failed: status === CellStatus.FAILED,
        });
        row[`_display_${column.id}`] = originalDisplayValue;
        row[`_source_${column.id}`] = usesSourceValue;
        row[`_has_source_${column.id}`] = sourceValue !== undefined;
        row[`_missing_${column.id}`] = isMissingInputValue;
        row[`_pendingHydration_${column.id}`] = isInputColumn
          ? isSourceLoading
          : isRunDetailLoading;
        row[`_status_${column.id}`] = status;
        row[`_error_${column.id}`] = errorMessage;
        row[`_value_${column.id}`] = rawValue;
      }
      return row;
    });

    const remainingInputRows = remainingInputSets;
    const parsedRowsToAdd = Number.parseInt(rowsToAddDraft, 10);
    const rowsToAdd = Math.min(
      Math.max(Number.isNaN(parsedRowsToAdd) ? 1 : parsedRowsToAdd, 1),
      Math.max(remainingInputRows, 1),
    );
    const inputSetNoun = rowsToAdd === 1 ? "input set" : "input sets";
    let addInputRowsDisabledReason: string | undefined;
    if (!canEdit) {
      addInputRowsDisabledReason = "Input sets can’t be changed while this evaluation is locked.";
    } else if (isAddingInputSets) {
      addInputRowsDisabledReason = "Input sets are being added.";
    } else if (remainingInputRows === 0) {
      addInputRowsDisabledReason = `You can keep up to ${maxInputSets} input sets.`;
    }
    const commitRowsToAddDraft = () => setRowsToAddDraft(String(rowsToAdd));
    const addInputRows = async () => {
      if (!canEdit || isAddingInputSets || remainingInputRows === 0) {
        return;
      }

      const addedSetIds = await addInputSets(rowsToAdd);
      if (addedSetIds.length > 0) {
        setRowsToAddDraft("1");
      }
    };

    const handleCellEditRequest = useCallback(
      (event: CellEditRequestEvent<EvaluateAdHocRow>) => {
        const variableSetId = event.data?._variableSetId;
        const columnId = event.column.getColId();
        const variableName = variableByColumnId.get(columnId);
        if (!variableSetId || !variableName || event.newValue === event.oldValue) {
          return;
        }

        const variableSet = playground.input_variable_sets.find(set => set.id === variableSetId);
        const variable = variableSet?.variables.find(candidate => candidate.key === variableName);
        const newValue =
          typeof event.newValue === "object" && event.newValue !== null
            ? event.newValue
            : String(event.newValue ?? "");
        const currentRow = event.node.data ?? event.data;
        event.node.setData({
          ...currentRow,
          [columnId]: toCellDisplayText(newValue),
          [`_display_${columnId}`]: toCellDisplayText(newValue),
          [`_source_${columnId}`]: false,
          [`_missing_${columnId}`]: false,
          [`_value_${columnId}`]: { value: newValue },
        });
        playground.updateInputVariable(variableSetId)(
          variable?.id ?? uuidv4(),
          variableName,
          newValue,
        );
      },
      [playground, variableByColumnId],
    );

    if (isSourceLoading) {
      return (
        <div className="space-y-2 p-3">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      );
    }

    return (
      <>
        <Sheet<EvaluateAdHocRow>
          className="pl-smart-table !h-auto min-w-0 max-w-full overflow-hidden [&_.ag-root-wrapper]:border-none"
          columns={sheetColumns}
          rowData={rows}
          getRowId={({ data }) => data.id}
          editing={EVALUATE_GRID_EDITING}
          selection={EVALUATE_GRID_SELECTION}
          display={EVALUATE_GRID_DISPLAY}
          rowNumbers={EVALUATE_GRID_ROW_NUMBERS}
          gridOptions={gridOptions}
          onCellEditRequest={handleCellEditRequest}
          onGridReady={params => {
            gridApiRef.current = params.api;
          }}
        />
        <div className="relative z-30 flex h-11 items-center gap-2 border-t border-border/50 bg-background/90 px-2 backdrop-blur supports-[backdrop-filter]:bg-background/75">
          <div
            className={cn(
              "inline-flex h-8 items-center overflow-hidden rounded-md border border-border/60",
              "bg-background text-[12px] shadow-sm transition-colors hover:border-border",
            )}
          >
            <BasicTooltip
              content={addInputRowsDisabledReason}
              shouldShow={Boolean(addInputRowsDisabledReason)}
              side="top"
            >
              <span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void addInputRows()}
                  disabled={Boolean(addInputRowsDisabledReason)}
                  className={cn(
                    "h-8 rounded-none border-r border-border/50 px-2.5 text-[12px] font-medium",
                    "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                  aria-label={`Add ${rowsToAdd} ${inputSetNoun}`}
                >
                  <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
                  {isAddingInputSets ? "Adding…" : "Add"}
                </Button>
              </span>
            </BasicTooltip>

            <label className="flex h-full items-center gap-1.5 px-2 text-muted-foreground">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={rowsToAddDraft}
                onChange={event => {
                  if (/^\d*$/.test(event.target.value)) {
                    setRowsToAddDraft(event.target.value);
                  }
                }}
                onBlur={commitRowsToAddDraft}
                onKeyDown={event => {
                  if (event.key === "Enter") {
                    commitRowsToAddDraft();
                    void addInputRows();
                  }
                }}
                onFocus={event => event.currentTarget.select()}
                disabled={!canEdit || isAddingInputSets || remainingInputRows === 0}
                aria-label="Number of input sets to add"
                title={`Add between 1 and ${Math.max(remainingInputRows, 1)} input sets`}
                className={cn(
                  "h-6 w-9 rounded-sm border-0 bg-muted/50 px-1 text-center",
                  "text-[12px] font-medium tabular-nums text-foreground outline-none",
                  "transition-colors focus:bg-background focus:ring-1 focus:ring-ring/30",
                )}
              />
              <span className="select-none tabular-nums">{inputSetNoun}</span>
            </label>
          </div>

          <span className="ml-auto text-[11px] text-muted-foreground">
            {maxInputSets === 0
              ? "No sheet rows available"
              : `${variableSets.length} of ${maxInputSets} input sets`}
          </span>
        </div>
        <CellContentDialog
          tableId={reference.smart_table_id}
          sheetId={reference.sheet_id}
          gridApiRef={gridApiRef}
          dialog={dialog}
          canEditCell={() => false}
        />
      </>
    );
  },
);

export default EvaluateAdHocSheet;
